import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { MdClose, MdSchedule, MdSend, MdArrowBack } from 'react-icons/md';
import ScheduleForm from './ScheduleForm';
import PostNowForm from './PostNowForm';
import { scheduleCampaignRequest, resetScheduleState } from '../../redux/slices/campaignScheduleSlice';
import { useRestaurantNav } from '../../hooks/useRestaurantNav';
import type { RootState } from '../../redux/store';
import type { ShareModalProps, ShareOption, ScheduleData, SocialMedium } from './types';

const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  flyerImageUrl,
  generatedContentId,
  flyerId,
  socialMediaData,
  restaurantId,
  campaignId,
  onPostNow,
  onSchedule,
  initialOption,
  initialScheduleData,
  mediaProperties,
  mediaType
}) => {
  const dispatch = useDispatch();
  const { navigate } = useRestaurantNav();
  const [selectedOption, setSelectedOption] = useState<ShareOption | null>(initialOption || null);

  // Get schedule state from Redux
  const { success, loading, error } = useSelector((state: RootState) => state.campaignSchedule);
  // Listen for schedule success and navigate
  useEffect(() => {
    if (success && !loading) {
      // Navigate to marketing calendar
      navigate('/marketing-calendar');
      
      // Reset state and close modal
      dispatch(resetScheduleState());
      onClose();
      setSelectedOption(null);
    }
  }, [success, loading, navigate, dispatch, onClose]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(resetScheduleState());
    };
  }, [dispatch]);

  const handleOptionSelect = (option: ShareOption) => {
    setSelectedOption(option);
  };

  const handlePostNowSubmit = (selectedPlatforms: SocialMedium[]) => {
    if (onPostNow) {
      onPostNow();
    }
    onClose();
    setSelectedOption(null);
  };

  const handleScheduleSubmit = (data: ScheduleData) => {
    const { fromDate, time, selectedMediums } = data;
    const localDateTime = new Date(`${fromDate}T${time}:00`);
    const scheduleDateTime = localDateTime.toISOString();
    // Prepare channels data
    const channels = selectedMediums.map((platform: SocialMedium) => ({
      name: platform.toLowerCase(),
      settings: {}
    }));

    // Dispatch Redux action if campaignId exists
    if (campaignId) {
      dispatch(scheduleCampaignRequest({
        campaignId: campaignId,
        scheduleData: {
          scheduleDate: scheduleDateTime,
          channels: channels
        }
      }));
    }
  };

  const handleBack = () => {
    setSelectedOption(null);
  };

  const handleClose = () => {
    setSelectedOption(null);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
            onClick={handleClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden"
              style={{
                backdropFilter: 'blur(10px)',
                boxShadow: 'rgba(0, 0, 0, 0.1) 0px 0px 14px 0px',
                background: 'linear-gradient(90deg, rgba(33, 33, 33, 0.92), rgba(55, 55, 55, 0.92))',
                border: '1px solid rgba(224, 224, 224, 0.13)'
              }}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-700/50 flex-shrink-0">
                <div className="flex items-center gap-3">
                  {selectedOption && (
                    <button
                      onClick={handleBack}
                      className="text-brand-primary hover:text-[#d63d20] transition-colors"
                    >
                      <MdArrowBack size={24} />
                    </button>
                  )}
                  <h2 className="text-2xl font-normal text-brand-primary">
                    {selectedOption === 'schedule-later' ? 'Schedule Campaign' : selectedOption === 'post-now' ? 'Post Now' : 'Share Campaign'}
                  </h2>
                </div>
                <button
                  onClick={handleClose}
                  className="text-brand-primary hover:text-[#d63d20] transition-colors"
                >
                  <MdClose size={28} />
                </button>
              </div>

              {/* Content */}
              <div className="p-4 overflow-y-auto flex-1">
                {!selectedOption ? (
                  // Option Selection View
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Post Now Option */}
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleOptionSelect('post-now')}
                      className="group relative overflow-hidden rounded-xl transition-all duration-300 p-8 bg-surface-dark"
                      style={{
                        border: "1px solid rgba(240, 238, 238, 0.13)",
                        backdropFilter: "blur(10px)",
                      }}
                    >
                      <div className="flex flex-col items-center text-center space-y-4">
                        <div className="w-16 h-16 rounded-full bg-brand-primary/10 flex items-center justify-center group-hover:bg-brand-primary/20 transition-colors">
                          <MdSend size={32} className="text-brand-primary" />
                        </div>
                        <h3 className="text-xl font-medium text-white">Post Now</h3>
                        <p className="text-gray-400 text-sm">
                          Share your campaign immediately to all selected platforms
                        </p>
                      </div>
                    </motion.button>

                    {/* Schedule Later Option */}
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleOptionSelect('schedule-later')}

                      className="group relative overflow-hidden rounded-xl transition-all duration-300 p-8 bg-surface-dark"
                      style={{
                        border: "1px solid rgba(240, 238, 238, 0.13)",
                        backdropFilter: "blur(10px)",
                      }}
                    >
                      <div className="flex flex-col items-center text-center space-y-4">
                        <div className="w-16 h-16 rounded-full bg-brand-primary/10 flex items-center justify-center group-hover:bg-brand-primary/20 transition-colors">
                          <MdSchedule size={32} className="text-brand-primary" />
                        </div>
                        <h3 className="text-xl font-medium text-white">Schedule Later</h3>
                        <p className="text-gray-400 text-sm">
                          Plan and schedule your campaign for a specific date and time
                        </p>
                      </div>
                    </motion.button>
                  </div>
                ) : selectedOption === 'post-now' ? (
                  // Post Now Form View
                  <PostNowForm
                    flyerImageUrl={flyerImageUrl}
                    generatedContentId={generatedContentId}
                    flyerId={flyerId}
                    socialMediaData={socialMediaData}
                    restaurantId={restaurantId}
                    campaignId={campaignId}
                    onSubmit={handlePostNowSubmit}
                    mediaProperties={mediaProperties}
                    mediaType={mediaType}
                  />
                ) : (
                  // Schedule Form View
                  <ScheduleForm
                    flyerImageUrl={flyerImageUrl}
                    generatedContentId={generatedContentId}
                    flyerId={flyerId}
                    socialMediaData={socialMediaData}
                    restaurantId={restaurantId}
                    campaignId={campaignId}
                    onSubmit={handleScheduleSubmit}
                    initialData={initialScheduleData}
                    isScheduling={loading}
                  />
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ShareModal;
